import {
    OverlayContainer,
    OverlayProvider,
    useOverlayPosition,
    useOverlayTrigger,
} from "@react-aria/overlays";
import React, { useEffect, useState } from "react";

import DatePickerCalendar from "shared/components/date-controls/date-picker/DatePickerCalendar";
import DatePickerDateField from "shared/components/date-controls/date-picker/DatePickerDateField";
import DatePickerPopOver from "shared/components/date-controls/date-picker/DatePickerPopOver";
import Icon from "shared/components/utils/Icon";
import PropTypes from "prop-types";
import { useDatePicker } from "@react-aria/datepicker";
import { useDatePickerState } from "@react-stately/datepicker";
import { useOverlayTriggerState } from "@react-stately/overlays";
import {
    StyledDateFieldWrapper,
    StyledBaseButtonWrapper,
    StyledBaseButton,
    DatePickerWrapper,
} from "./DatePickerMain.styles";

const DatePickerMain = ({
    disabled = false,
    feedbackType,
    dateOnChange,
    isSmallSize = false,
    ...props
}) => {
    const datePickerState = useDatePickerState(props);
    const ref = React.useRef();
    const { groupProps, fieldProps, buttonProps, dialogProps, calendarProps } =
        useDatePicker(props, datePickerState, ref);

    const overlayState = useOverlayTriggerState({});

    const triggerRef = React.useRef();
    const overlayRef = React.useRef();

    const { triggerProps, overlayProps } = useOverlayTrigger(
        { type: "dialog" },
        overlayState,
        triggerRef
    );

    const { overlayProps: positionProps } = useOverlayPosition({
        targetRef: triggerRef,
        overlayRef,
        placement: "bottom",
        offset: 6,
        isOpen: overlayState.isOpen,
    });

    const handleOnClickCalendar = () => {
        overlayState.setOpen(!overlayState.isOpen);
        datePickerState.setOpen(!datePickerState.isOpen);
    };

    const [confirmedDateValue, setConfirmedDateValue] = useState(null);

    useEffect(() => {
        setConfirmedDateValue(datePickerState.dateValue);
        // Mount-only: initializes the "last confirmed value" used by Cancel to revert.
        // Adding datePickerState.dateValue would re-confirm on every picker change,
        // making Cancel always revert to the current value instead of the last saved one.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        !overlayState.isOpen &&
            dateOnChange?.year.toString().length === 4 &&
            setConfirmedDateValue(dateOnChange);
    }, [dateOnChange, overlayState.isOpen]);

    const handleOnClickCancel = () => {
        datePickerState.setDateValue(confirmedDateValue);
        overlayState.setOpen(false);
    };

    const handleOnClickDone = () => {
        setConfirmedDateValue(datePickerState.dateValue);
        datePickerState.setDateValue(datePickerState.dateValue);
        overlayState.setOpen(false);
    };

    return (
        <DatePickerWrapper>
                <StyledDateFieldWrapper
                    {...groupProps}
                    ref={ref}
                    feedbackType={feedbackType}
                    disabled={disabled}
                    isSmallSize={isSmallSize}
                >
                    <DatePickerDateField
                        disabled={disabled}
                        isSmallSize={isSmallSize}
                        {...fieldProps}
                    />
                    <StyledBaseButtonWrapper
                        ref={triggerRef}
                    ></StyledBaseButtonWrapper>
                    {disabled ? (
                        <StyledBaseButton disabled={disabled}>
                            <Icon name="calendar" />
                        </StyledBaseButton>
                    ) : (
                        <StyledBaseButton
                            type="button"
                            onClick={handleOnClickCalendar}
                            {...Object.entries(buttonProps).splice(
                                Object.keys(buttonProps).indexOf("onPress"),
                                1
                            )}
                            {...Object.entries(triggerProps).splice(
                                Object.keys(triggerProps).indexOf("onPress"),
                                1
                            )}
                        >
                            <Icon name="calendar" />
                        </StyledBaseButton>
                    )}
                </StyledDateFieldWrapper>

                {!disabled && (
                    <OverlayProvider>
                        {overlayState.isOpen && (
                            <OverlayContainer>
                                    <DatePickerPopOver
                                        {...dialogProps}
                                        {...overlayProps}
                                        {...positionProps}
                                        ref={overlayRef}
                                        isOpen={overlayState.isOpen}
                                        onClose={handleOnClickCancel}
                                    >
                                        <DatePickerCalendar
                                            {...calendarProps}
                                            onClickCancel={handleOnClickCancel}
                                            onClickDone={handleOnClickDone}
                                        />
                                    </DatePickerPopOver>
                                </OverlayContainer>
                        )}
                    </OverlayProvider>
                )}
            </DatePickerWrapper>
    );
};

DatePickerMain.propTypes = {
    disabled: PropTypes.bool,
    feedbackType: PropTypes.string,
    dateOnChange: PropTypes.func,
    isSmallSize: PropTypes.bool,
    value: PropTypes.any,
    onChange: PropTypes.func,
};

export default DatePickerMain;
